import { resolveDerivedPageImageFileRef } from 'features/knowledgeDocumentViewer/utils/documentPageImages'
import type { MetadataEvalSampledPage } from 'features/knowledgeDocumentViewer/utils/pageMetadataEvalSampling'
import {
  type EvalPageTrail,
  evalTrailPageAtCursor,
} from 'features/knowledgeDocumentViewer/utils/pageMetadataEvalTrail'
import type { RolodexFilePath } from 'types'

export const EVAL_PREFETCH_LOOKAHEAD = 8

type PrefetchedImageState = {
  url: string | undefined
  isLoading: boolean
  warmedUrl: string | undefined
}

export function evalPageImagePrefetchSettled(
  hiRes: boolean,
  high: PrefetchedImageState,
  thumb: PrefetchedImageState
): boolean {
  const settled = ({ url, isLoading, warmedUrl }: PrefetchedImageState) =>
    url ? warmedUrl === url : !isLoading
  return (!hiRes || settled(high)) && settled(thumb)
}

export function selectActivePrefetchJobs(
  trail: EvalPageTrail,
  queuedPages: readonly MetadataEvalSampledPage[],
  completed: ReadonlySet<string>
) {
  const currentPage = evalTrailPageAtCursor(trail)
  const upcoming = [
    ...trail.visited.slice(trail.cursor + 1),
    ...queuedPages,
  ].slice(0, EVAL_PREFETCH_LOOKAHEAD)
  const jobs = [...(currentPage ? [currentPage] : []), ...upcoming].map(
    (page, index) => ({
      page,
      hiRes: index < 4,
      jobId: `${page.page_id}:${index < 4}`,
    })
  )
  let backgroundJobs = 0
  return jobs.filter(({ jobId }, index) => {
    if (index === 0 || completed.has(jobId)) return true
    return backgroundJobs++ < 2
  })
}

export function evalPagePrefetchImageRefs(args: {
  siteId: string
  documentId: string
  pageNum: number
}): {
  hiRes: RolodexFilePath | undefined
  thumbnail: RolodexFilePath | undefined
} {
  return {
    hiRes: resolveDerivedPageImageFileRef({
      siteId: args.siteId,
      bqDocumentId: args.documentId,
      pageNumber: args.pageNum,
      variant: 'hiRes',
    }),
    thumbnail: resolveDerivedPageImageFileRef({
      siteId: args.siteId,
      bqDocumentId: args.documentId,
      pageNumber: args.pageNum,
      variant: 'thumbnail',
    }),
  }
}

export function startImageWarm(
  url: string,
  onSettled?: () => void
): () => void {
  const img = new Image()
  img.onload = onSettled ?? null
  img.onerror = onSettled ?? null
  img.src = url
  return () => {
    img.onload = null
    img.onerror = null
    img.src = ''
  }
}
